import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, finalize, forkJoin, map, Observable, of, Subject, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService, SortEvent } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { PaginatorState } from 'primeng/paginator';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, EnumValues } from '@card-software/library';
import { Business, BusinessEntityType } from '@app/models/business';
import { BusinessProxyService } from '@app/proxies/finance/business.proxy.service';
import { WorkOrder, WorkOrderSearchCommand } from '../../../../models/order/work-order';
import { ProductionCard } from '../../../../models/production/production-card';
import { WorkOrderProxyService } from '../../../../proxies/order/work-order.proxy.service';
import { ProductionCardsProxyService } from '../../../../proxies/production/production-card.proxy.service';
import { EnumService } from '../../../../services/enum.service';

@Component({
  selector: 'app-work-orders',
  templateUrl: './work-orders.component.html',
  styleUrl: './work-orders.component.scss',
})
export class WorkOrdersComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private workOrderProxyService = inject(WorkOrderProxyService);
  private businessProxyService = inject(BusinessProxyService);
  private productionCardsProxyService = inject(ProductionCardsProxyService);
  private enumService = inject(EnumService);
  public messageService = inject(MessageService);

  // Signals
  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  workOrders = signal<WorkOrder[]>([]);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(20);

  // Sorting
  sortField = signal<string | undefined>(undefined);
  sortOrder = signal<number>(-1);

  listBrands = signal<Business[]>([]);
  listCustomers = signal<Business[]>([]);

  filteredBrands = signal<Business[]>([]);
  filteredCustomers = signal<Business[]>([]);

  // State
  orderType = 0;
  orderMenuitems: MenuItem[] = [];
  workOrderProductionCard: Record<string, ProductionCard[]> = {};
  private currentSearchCriteria: WorkOrderSearchCommand = {};
  readonly filterState = createDynamicFilterState({ storageKey: 'work-orders-filters', config: this.filterConfig });
  showFilters = signal(false);
  private filtersPanelInitialized = false;

  private destroy$ = new Subject<void>();

  // Enums
  public orderTypesEnums: EnumValues;
  public orderStatusEnums: EnumValues;
  orderTypesOptionsForSelect: { label: string; value: number }[] = [];

  customersMap: Record<string, Business> = {};
  brandsMap: Record<string, Business> = {};

  translateFilter = effect(
    () => {
      if (this.translateService.currentLang) {
        this.tryInitializeFilterConfig();
      }
    },
    { allowSignalWrites: true },
  );

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => {
      if (!this.filtersPanelInitialized) {
        this.filtersPanelInitialized = true;
        this.showFilters.set(this.filterState.hasActive());
      }
      this.searchWithFilters(filters);
    });

    this.orderTypesEnums = Object.values(this.enumService.getEnum('WorkOrderType', 'orderEnums')?.values || {});
    this.orderStatusEnums = Object.values(this.enumService.getEnum('WorkOrderStatus', 'orderEnums')?.values || {});
    this.setOrderTypeMenu();
  }

  ngOnInit(): void {
    this.orderTypesOptionsForSelect = this._setOptionsForPrimeNg(this.orderTypesEnums).filter(
      (option) => option.label !== 'Greige' && option.label !== 'BGrade',
    );
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getRowData(rowData: unknown): WorkOrder {
    return rowData as WorkOrder;
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'orderNumber',
          label: translate('Order_Number', 'Order Number'),
          placeholder: translate('Enter_order_number', 'Enter order number'),
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'customerProductOrder',
          label: translate('Customer_Product_Order', 'Customer Product Order'),
          placeholder: translate('Enter_customer_product_order', 'Enter customer product order'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'orderType',
          label: translate('Order_Type', 'Order Type'),
          placeholder: translate('Select_order_type', 'Select order type'),
          options: this.orderTypesOptionsForSelect,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'customerReference',
          label: translate('Customer', 'Customer'),
          placeholder: translate('Select_customer', 'Select customer'),
          autocompleteConfig: {
            suggestions: this.filteredCustomers(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterCustomers(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
          },
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'autocomplete',
          formControlName: 'brandReference',
          label: translate('Brand', 'Brand'),
          placeholder: translate('Select_brand', 'Select brand'),
          autocompleteConfig: {
            suggestions: this.filteredBrands(),
            optionLabel: 'fieldToDisplay',
            optionValue: 'id',
            completeMethod: (event: AutoCompleteCompleteEvent) => this.filterBrands(event),
            resolveById: (id: string) => this.resolveBusiness(id),
            dropdown: true,
          },
          showClear: true,
          defaultVisible: true,
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private resolveBusiness(id: string): Observable<Business | null> {
    return this.businessProxyService.getBusinessById(id).pipe(
      map((response) => (response.data ? { ...response.data, fieldToDisplay: `(${response.data.code}) ${response.data.name}` } : null)),
      catchError((error: HttpErrorResponse) => (error.status === 404 ? of(null) : throwError(() => error))),
    );
  }

  loadInitialData(): void {
    this.startLoading();
    forkJoin({
      businesses: this.businessProxyService.search({
        businessTypes: [BusinessEntityType.Brand, BusinessEntityType.Client],
      }),
    })
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: ({ businesses }) => {
          const businessData = businesses.data?.items || [];

          const allCustomers = businessData
            .filter((b) => b.businessTypes?.includes(BusinessEntityType.Client))
            .map((customer) => ({
              ...customer,
              fieldToDisplay: `(${customer.code}) ${customer.name}`,
            }));

          const allBrands = businessData
            .filter((b) => b.businessTypes?.includes(BusinessEntityType.Brand))
            .map((brand) => ({
              ...brand,
              fieldToDisplay: `(${brand.code}) ${brand.name}`,
            }));

          this.listCustomers.set(allCustomers);
          this.listBrands.set(allBrands);
          this.filteredCustomers.set(allCustomers);
          this.filteredBrands.set(allBrands);

          this.customersMap = this.listCustomers().reduce(
            (acc, customer) => {
              if (customer.id) {
                acc[customer.id] = customer;
              }
              return acc;
            },
            {} as Record<string, Business>,
          );

          this.brandsMap = this.listBrands().reduce(
            (acc, brand) => {
              if (brand.id) {
                acc[brand.id] = brand;
              }
              return acc;
            },
            {} as Record<string, Business>,
          );
        },
        error: (e) => {
          console.error('Error fetching businesses:', e);
        },
      });
  }

  setOrderTypeMenu() {
    this.orderMenuitems = this._setOptionsForPrimeNg(this.orderTypesEnums)
      .filter((orderType) => orderType.label !== 'GreigeStockForDyeing' && orderType.label !== 'GreigeOnly' && orderType.label !== 'BGrade')
      .map((orderType) => {
        return {
          label: orderType.label,
          command: () => {
            this.orderType = orderType.value;
            this.redirectToDetails('new', true);
          },
        };
      });
  }

  filterBrands(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.listBrands().filter(
      (brand) => brand.name?.toLowerCase().includes(query) || brand.code?.toLowerCase().includes(query),
    );
    this.filteredBrands.set(filtered);
  }

  filterCustomers(event: AutoCompleteCompleteEvent): void {
    const query = event.query.toLowerCase();
    const filtered = this.listCustomers().filter(
      (customer) => customer.name?.toLowerCase().includes(query) || customer.code?.toLowerCase().includes(query),
    );
    this.filteredCustomers.set(filtered);
  }

  getCustomerName(customerReference?: string | null): string {
    if (!customerReference) return '';
    return this.customersMap[customerReference]?.name || customerReference;
  }

  getBrandName(brandReference?: string | null): string {
    if (!brandReference) return '';
    return this.brandsMap[brandReference]?.name || brandReference;
  }

  getCustomerProductOrder(workOrder: WorkOrder): string {
    return workOrder.customerProductOrders.map((order) => order.productOrderNumber).join(', ');
  }

  redirectToDetails(orderId: string, orderTypeActive = false): void {
    const queryParams = orderTypeActive
      ? { id: orderId, orderType: this.orderType, workOrderId: orderId }
      : { id: orderId, workOrderId: orderId };
    this.router.navigate(['/production-cards-details'], { queryParams });
  }

  getAllProductionCards(): void {
    const workOrdersList = this.workOrders();
    if (workOrdersList.length === 0) return;

    const workOrderIds = workOrdersList.map((wo) => wo.id);

    this.productionCardsProxyService.getProductionCardByWorkOrderReferences(workOrderIds).subscribe({
      next: (response) => {
        if (response.data) {
          this.workOrderProductionCard = response.data;
        }
      },
      error: (e) => {
        console.error('Error fetching production cards:', e);
      },
    });
  }

  getWorkOrders(searchCriteria: WorkOrderSearchCommand): void {
    this.startLoading();
    const statuses = [2, 3, 4, 5, 6];
    const form = { ...searchCriteria, statuses };

    this.workOrderProxyService
      .search(form, this.currentPage(), this.pageSize(), this.sortField(), this.sortOrder())
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            // Filter out GreigeStockForDyeing(2), BGrade(5) and GreigeOnly(6) work orders
            const filteredOrders = response.data.items.filter(
              (order) =>
                order.orderType !== 2 && // GreigeStockForDyeing
                order.orderType !== 5 && // BGrade
                order.orderType !== 6, // GreigeOnly
            );
            this.workOrders.set(filteredOrders);
            this.totalRecords.set(response.data.totalCount);
          } else {
            this.workOrders.set([]);
            this.totalRecords.set(0);
          }
          this.getAllProductionCards();
        },
        error: (e) => {
          console.error('Error fetching work orders:', e);
          this.workOrders.set([]);
          this.totalRecords.set(0);
        },
      });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const getBusinessId = (value: unknown): string => {
      if (!value) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'object' && value !== null && 'id' in value) return (value as { id: string }).id;
      return '';
    };

    const searchCriteria: WorkOrderSearchCommand = {
      orderNumber: (filters['orderNumber'] as string) || '',
      orderType: filters['orderType'] != null ? (filters['orderType'] as number) : undefined,
      customerReference: getBusinessId(filters['customerReference']),
      brandReference: getBusinessId(filters['brandReference']),
      customerProductOrder: (filters['customerProductOrder'] as string) || '',
    };

    this.currentSearchCriteria = searchCriteria;
    this.currentPage.set(1);
    this.getWorkOrders(searchCriteria);
  }

  getWorkOrderPlannedAmount(workOrder: WorkOrder): number {
    const productionCards = this.workOrderProductionCard[workOrder.id];
    if (productionCards) {
      return productionCards.reduce((acc, productionCard) => {
        return (
          acc +
          productionCard.productionCardEntries.reduce((entryAcc, entry) => {
            return entryAcc + entry.quantity;
          }, 0)
        );
      }, 0);
    }
    return 0;
  }

  getWorkOrderNeededAmount(workOrder: WorkOrder): number {
    return workOrder.fabrics.reduce((acc, fabric) => {
      return acc + fabric.quantityInUOM;
    }, 0);
  }

  getMissingAmount(workOrder: WorkOrder): number {
    const planned = this.getWorkOrderPlannedAmount(workOrder);
    const needed = this.getWorkOrderNeededAmount(workOrder);
    return needed - planned;
  }

  onSort(event: SortEvent): void {
    const newField = event.field;
    const newOrder = event.order ?? -1;

    if (newField === this.sortField() && newOrder === this.sortOrder()) {
      return;
    }

    this.sortField.set(newField);
    this.sortOrder.set(newOrder);
    this.currentPage.set(1);
    this.getWorkOrders(this.currentSearchCriteria);
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage.set((event.page || 0) + 1);
    this.pageSize.set(event.rows || this.pageSize());
    this.getWorkOrders(this.currentSearchCriteria);
  }

  private _setOptionsForPrimeNg(value: EnumValues): { label: string; value: number }[] {
    return Object.keys(value).map((key) => {
      const numKey = parseInt(key);
      return {
        label: value[numKey],
        value: numKey,
      };
    });
  }
}
